import * as React from 'react';
import { clsx } from 'clsx';

export interface SubSectionHeadingProps {
  /**
   * The heading text (required)
   */
  title: string;
  /**
   * Show an info icon next to the heading text
   * @default false
   */
  icon?: boolean;
  /**
   * Button variant to display on the right
   * - 'primary': Green button with white text
   * - 'tertiary': Gray button with dark text
   * - undefined: No button
   */
  button?: 'primary' | 'tertiary';
  /**
   * Text to display in the button (required if button is specified)
   */
  buttonText?: string;
  /**
   * Icon to display in the button (renders before button text)
   */
  buttonIcon?: React.ReactNode;
  /**
   * Click handler for the button
   */
  onButtonClick?: () => void;
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Additional inline styles
   */
  style?: React.CSSProperties;
}

// Info icon (circle with i)
const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="6.5" stroke="#2f2f2f" strokeWidth="1.2" />
    <path
      d="M8 7V11"
      stroke="#2f2f2f"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
    <circle cx="8" cy="5" r="0.75" fill="#2f2f2f" />
  </svg>
);

// Default plus icon for buttons
const PlusIcon = ({ color = '#2f2f2f' }: { color?: string }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8 3V13M3 8H13"
      stroke={color}
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Arbor Design System sub-section heading styles
const subSectionHeadingStyles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    borderBottom: '1px solid #efefef',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    boxSizing: 'border-box' as const,
  },
  containerNoButton: {
    padding: '12px 8px',
    gap: '0px',
  },
  containerWithButton: {
    padding: '8px',
    gap: '12px',
    justifyContent: 'space-between',
  },
  textContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: 1,
    minHeight: '16px',
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#2f2f2f',
    lineHeight: '1.25',
    margin: 0,
    whiteSpace: 'nowrap' as const,
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
    flexShrink: 0,
  },
  buttonBase: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    height: '32px',
    minHeight: '32px',
    padding: '0 16px',
    borderRadius: '99px',
    border: 'none',
    cursor: 'pointer',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontSize: '13px',
    fontWeight: '600',
    lineHeight: '1.5',
    whiteSpace: 'nowrap' as const,
    flexShrink: 0,
    transition: 'background-color 0.15s ease-in-out, opacity 0.15s ease-in-out',
  },
  buttonPrimary: {
    backgroundColor: '#0e8a0e',
    color: '#ffffff',
  },
  buttonPrimaryHover: {
    backgroundColor: '#0a6b0a',
  },
  buttonTertiary: {
    backgroundColor: '#efefef',
    color: '#2f2f2f',
  },
  buttonTertiaryHover: {
    backgroundColor: '#e5e5e5',
  },
  buttonIconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
    flexShrink: 0,
  },
};

/**
 * SubSectionHeading component - Arbor Design System
 *
 * A sub-section heading component for dividing content within sections.
 * Supports optional icon next to text and optional button on the right.
 *
 * @example
 * ```tsx
 * // Basic sub-section heading
 * <SubSectionHeading title="Details" />
 *
 * // With info icon
 * <SubSectionHeading title="Details" icon />
 *
 * // With primary button
 * <SubSectionHeading
 *   title="Items"
 *   button="primary"
 *   buttonText="Add Item"
 *   onButtonClick={() => handleAdd()}
 * />
 *
 * // With tertiary button and icon
 * <SubSectionHeading
 *   title="Settings"
 *   icon
 *   button="tertiary"
 *   buttonText="Configure"
 *   onButtonClick={() => handleConfigure()}
 * />
 * ```
 */
export const SubSectionHeading = React.forwardRef<HTMLDivElement, SubSectionHeadingProps>(
  (
    {
      title,
      icon = false,
      button,
      buttonText = 'Button Text',
      buttonIcon,
      onButtonClick,
      className,
      style,
      ...props
    },
    ref
  ) => {
    const [isButtonHovered, setIsButtonHovered] = React.useState(false);

    const containerStyle: React.CSSProperties = {
      ...subSectionHeadingStyles.container,
      ...(button
        ? subSectionHeadingStyles.containerWithButton
        : subSectionHeadingStyles.containerNoButton),
      ...style,
    };

    const getButtonStyle = (): React.CSSProperties => {
      const baseStyle = { ...subSectionHeadingStyles.buttonBase };

      if (button === 'primary') {
        return {
          ...baseStyle,
          ...subSectionHeadingStyles.buttonPrimary,
          ...(isButtonHovered && subSectionHeadingStyles.buttonPrimaryHover),
        };
      }

      if (button === 'tertiary') {
        return {
          ...baseStyle,
          ...subSectionHeadingStyles.buttonTertiary,
          ...(isButtonHovered && subSectionHeadingStyles.buttonTertiaryHover),
        };
      }

      return baseStyle;
    };

    const renderButtonIcon = () => {
      if (buttonIcon) {
        return (
          <span style={subSectionHeadingStyles.buttonIconWrapper}>{buttonIcon}</span>
        );
      }
      // Default icon based on button type
      return (
        <span style={subSectionHeadingStyles.buttonIconWrapper}>
          <PlusIcon color={button === 'primary' ? '#ffffff' : '#2f2f2f'} />
        </span>
      );
    };

    return (
      <div
        ref={ref}
        className={clsx('arbor-sub-section-heading', className)}
        style={containerStyle}
        {...props}
      >
        <div style={subSectionHeadingStyles.textContainer}>
          <h3 style={subSectionHeadingStyles.title}>{title}</h3>
          {icon && (
            <span style={subSectionHeadingStyles.iconWrapper}>
              <InfoIcon />
            </span>
          )}
        </div>
        {button && (
          <button
            type="button"
            style={getButtonStyle()}
            onClick={onButtonClick}
            onMouseEnter={() => setIsButtonHovered(true)}
            onMouseLeave={() => setIsButtonHovered(false)}
          >
            {renderButtonIcon()}
            <span>{buttonText}</span>
          </button>
        )}
      </div>
    );
  }
);

SubSectionHeading.displayName = 'SubSectionHeading';
